'use client'

import type { ILandingCarouselSlugAssets } from '@/features/landing-page/helpers/get-carousel-assets-server'
import { FALLBACK_ASSET_SLUG } from '@/features/landing-page/helpers/landing-route-content'
import { createContext, useContext } from 'react'

export interface ILandingCarouselAssetsContextValue {
    /** Slug-keyed carousel data. The `home` slug is the universal fallback. */
    bySlug: Record<string, ILandingCarouselSlugAssets>
}

const LandingCarouselAssetsContext = createContext<ILandingCarouselAssetsContextValue | null>(null)

export function useLandingCarouselAssets() {
    return useContext(LandingCarouselAssetsContext)
}

/**
 * Resolve carousel assets for a slug. Returns the slug-specific entry when present and populated,
 * otherwise falls back to `home`. Returns null only when the context itself is missing.
 */
export function useLandingCarouselAssetsForSlug(slug: string): ILandingCarouselSlugAssets | null {
    const ctx = useContext(LandingCarouselAssetsContext)
    if (!ctx) return null
    const fromSlug = ctx.bySlug[slug]
    if (fromSlug?.assets.length) return fromSlug
    return ctx.bySlug[FALLBACK_ASSET_SLUG] ?? null
}

export function LandingCarouselAssetsProvider({
    value,
    children,
}: {
    value: ILandingCarouselAssetsContextValue
    children: React.ReactNode
}) {
    return <LandingCarouselAssetsContext.Provider value={value}>{children}</LandingCarouselAssetsContext.Provider>
}
